/*
 * Code is taken from uitk react gallery carousel and is modified to have
 * image attribution. To be added into uitk react once attribution component
 * get added into the library
 */

import * as React from "react";
import * as Swipeable from "react-swipeable";
import { LocalizedComponentProps } from "bernie-l10n";
import { withLocalization } from "@shared-ui/localization-context";
import { localAnalyticsIdType } from "uitk-react-gallery-carousel";
import { AnalyticsConfig, classNames, Level, liveAnnounce } from "uitk-react-utils";
import { UitkButton, UitkPagingNextButton, UitkPagingPrevButton } from "uitk-react-button";
import { UitkIcon } from "uitk-react-icons";
import { UitkScrim, UitkScrimType } from "uitk-react-scrim";

import { UitkGalleryCarouselItem } from "./UitkGalleryCarouselItem";

export interface ImageViewModel {
  caption?: string;
  src?: string;
  alt?: string;
  thumbnailSrc?: string;
  fallBackImage?: string;
  element?: any;
  source?: string;
  origin?: string;
  attribUrl?: string;
  attribRequired?: boolean;
  authorName?: string;
  authorUrl?: string;
  licenseSuite?: string;
  licenseName?: string;
  licenseUrl?: string;
  originalMediaUrl?: string;
}

export interface UitkGalleryCarouselProps extends LocalizedComponentProps {
  allyHeadingLevel?: number;
  allyHeadingText: string;
  analytics?: AnalyticsConfig;
  carouselName: string;
  className?: string;
  currentIndex?: number;
  images: ImageViewModel[];
  onImageClick?: () => void;
  onIndexChange?: (index: number) => void;
  onMediaButtonClick?: (index: number) => void;
  showGalleryButton?: boolean;
}

interface UitkGalleryCarouselState {
  currentIndex: number;
  isAnimating?: boolean;
  isAnimatingNext?: boolean;
  isAnimatingPrev?: boolean;
  isInteractive: boolean;
}

@withLocalization
export class UitkGalleryCarousel extends React.Component<UitkGalleryCarouselProps, UitkGalleryCarouselState> {
  public static displayName = "UitkGalleryCarousel";
  private nextprevCallbackTimer: number = 250;

  public static defaultProps: Partial<UitkGalleryCarouselProps> = {
    currentIndex: 0,
    showGalleryButton: true,
  };

  constructor(props: UitkGalleryCarouselProps) {
    super(props);

    this.state = {
      currentIndex: this.props.currentIndex || 0,
      isInteractive: false,
    };

    this.onImageClickCallback = this.onImageClickCallback.bind(this);
    this.onNextCallback = this.onNextCallback.bind(this);
    this.onPrevCallback = this.onPrevCallback.bind(this);
    this.onUpdateCallback = this.onUpdateCallback.bind(this);
    this.onMediaButtonCallback = this.onMediaButtonCallback.bind(this);
  }

  private animationTimer: any;

  // getter / setter for this.currentIndex
  get currentIndex() {
    return this.state.currentIndex;
  }

  set currentIndex(value) {
    this.setState({ currentIndex: value });
  }

  private onPrevCallback(): void {
    if (this.state.isAnimating) {
      return;
    }

    let newIndex = this.currentIndex - 1;
    const imagesLength: number = this.props.images.length;

    if (newIndex < 0) {
      newIndex = imagesLength - 1;
    }

    this.setState({
      isAnimating: true,
      isAnimatingNext: false,
      isAnimatingPrev: true,
      isInteractive: false,
    });

    if (this.animationTimer) {
      clearInterval(this.animationTimer);
    }

    this.animationTimer = setTimeout(() => {
      this.announce(newIndex);
      this.onUpdateCallback(newIndex);
    }, this.nextprevCallbackTimer);
  }

  private onNextCallback(): void {
    if (this.state.isAnimating) {
      return;
    }

    let newIndex: number = this.currentIndex + 1;
    const imagesLength: number = this.props.images.length;

    if (newIndex + 1 > imagesLength) {
      newIndex = 0;
    }

    this.setState({
      isAnimating: true,
      isAnimatingNext: true,
      isAnimatingPrev: false,
      isInteractive: false,
    });

    if (this.animationTimer) {
      clearInterval(this.animationTimer);
    }

    this.animationTimer = setTimeout(() => {
      this.announce(newIndex);
      this.onUpdateCallback(newIndex);
    }, this.nextprevCallbackTimer);
  }

  private onUpdateCallback(currentIndex: number): void {
    const onIndexChange = this.props.onIndexChange;

    if (typeof onIndexChange === "function") {
      onIndexChange(currentIndex);
    }
  }

  private onImageClickCallback(): void {
    const onImageClick = this.props.onImageClick;

    if (typeof onImageClick === "function") {
      onImageClick();
    }
  }

  private onMediaButtonCallback(): void {
    const onMediaButtonClick = this.props.onMediaButtonClick;

    if (typeof onMediaButtonClick === "function") {
      onMediaButtonClick(this.currentIndex + 1);
    }
  }

  private getForwardIndex(count: number): number {
    const images = this.props.images;

    if (images && images.length > 0 && count > 0) {
      const softIndex = this.currentIndex + count;

      return Math.abs(softIndex % images.length);
    }

    return this.currentIndex;
  }

  private getBackwardIndex(count: number): number {
    const images = this.props.images;

    if (images && images.length > 0 && count > 0) {
      if (images.length === 1) {
        return 0;
      }

      const softIndex = this.currentIndex - count;

      if (softIndex >= 0) {
        return softIndex;
      }

      if (Math.abs(softIndex % images.length) === 0) {
        return 0;
      }

      return Math.abs(images.length - Math.abs(softIndex % images.length));
    }

    return 0;
  }

  private announce(index: number): void {
    let captionToBeRead: string | undefined;

    if (this.props.images[index].caption !== undefined) {
      captionToBeRead = this.props.images[index].caption;
    } else if (this.props.images[index].alt !== undefined) {
      captionToBeRead = this.props.images[index].alt;
    } else {
      captionToBeRead = "";
    }

    this.setState(
      {
        currentIndex: index,
        isAnimating: false,
        isAnimatingNext: false,
        isAnimatingPrev: false,
        isInteractive: true,
      },
      () =>
        liveAnnounce(
          this.props.l10n.formatText(
            "gallerycarousel.imageChange",
            this.state.currentIndex + 1,
            this.props.images.length,
            captionToBeRead
          ),
          Level.POLITE
        )
    );
  }

  public componentDidMount(): void {
    this.setState({
      isAnimating: false,
      isInteractive: true,
    });
  }

  public UNSAFE_componentWillReceiveProps(nextProps: any): void {
    const nextCurrIndex = nextProps.currentIndex;

    if (nextCurrIndex !== this.state.currentIndex) {
      this.setState({ currentIndex: nextCurrIndex });
    }
  }

  public shouldComponentUpdate(nextProps: any, nextState: any): boolean {
    const { currentIndex, images } = this.props;
    const nextImages = nextProps.images;

    // only re-render when images, isAnimating or currentIndex change
    const imageLength = images && images.length;
    const nextImageLength = nextImages && nextImages.length;
    if (imageLength !== nextImageLength) {
      return true;
    }
    if (this.state.isAnimating !== nextState.isAnimating) {
      return true;
    }
    if (currentIndex !== nextProps.currentIndex) {
      return true;
    }

    return false;
  }

  public render() {
    const {
      allyHeadingLevel,
      allyHeadingText,
      analytics,
      carouselName,
      className,
      images,
      showGalleryButton,
    } = this.props;
    const { currentIndex, isAnimating, isAnimatingNext, isAnimatingPrev, isInteractive } = this.state;

    let figureAnimationClass: string = "";
    const baseCssClass = classNames("uitk-gallery-carousel", className);

    const imagesLength: number = images.length;
    const canSwipe: boolean = isInteractive && images && imagesLength > 1;

    const HeadingLevel: any = allyHeadingLevel ? `h${allyHeadingLevel}` : `h3`;
    const { formatText } = this.props.l10n;

    if (isAnimating) {
      if (isAnimatingPrev) {
        figureAnimationClass = "uitk-gallery-carousel-prev";
      } else if (isAnimatingNext) {
        figureAnimationClass = "uitk-gallery-carousel-next";
      }
    }

    const clickLeftAnalytics = analytics && {
      localAnalyticsId: `${carouselName}.GalleryCarousel.${localAnalyticsIdType.clickLeft}`,
      ...analytics,
    };

    const clickRightAnalytics = analytics && {
      localAnalyticsId: `${carouselName}.GalleryCarousel.${localAnalyticsIdType.clickRight}`,
      ...analytics,
    };

    const clickMediaGalleryAnalytics = analytics && {
      localAnalyticsId: `${carouselName}.MediaGalleryTrigger`,
      ...analytics,
    };

    return (
      images &&
      imagesLength > 0 && (
        <div className={baseCssClass}>
          <HeadingLevel className="is-visually-hidden">{allyHeadingText}</HeadingLevel>
          <Swipeable
            className={`uitk-gallery-carousel-items ratio-16-9 uitk-grid ${figureAnimationClass}`}
            onSwipingLeft={canSwipe ? this.onNextCallback : undefined}
            onSwipingRight={canSwipe ? this.onPrevCallback : undefined}
            onClick={this.onImageClickCallback}
            delta={70} // the amount of px before we start firing events. default is 10.
            disabled={!canSwipe} // disable the swipe when animating so as to not fire twice and infinitely scroll
          >
            <UitkGalleryCarouselItem
              {...images[this.getBackwardIndex(1)]}
              index={this.getBackwardIndex(1)}
              className="uitk-gallery-carousel-item-prev"
            />

            <UitkGalleryCarouselItem
              {...images[this.currentIndex]}
              index={this.currentIndex}
              className="uitk-gallery-carousel-item-current"
            />

            <UitkGalleryCarouselItem
              {...images[this.getForwardIndex(1)]}
              index={this.getForwardIndex(1)}
              className="uitk-gallery-carousel-item-next"
            />

            <UitkGalleryCarouselItem
              {...images[this.getForwardIndex(2)]}
              index={this.getForwardIndex(2)}
              className="uitk-gallery-carousel-item-200"
            />

            <UitkGalleryCarouselItem
              {...images[this.getForwardIndex(3)]}
              index={this.getForwardIndex(3)}
              className="uitk-gallery-carousel-item-300"
            />

            <UitkGalleryCarouselItem
              {...images[this.getForwardIndex(4)]}
              index={this.getForwardIndex(4)}
              className="uitk-gallery-carousel-item-400"
            />

            <UitkScrim className="uitk-gallery-carousel-scrim" tag="div" type={UitkScrimType.BOTTOM} />
          </Swipeable>

          <UitkPagingPrevButton
            className="uitk-gallery-carousel-button-prev"
            iconId="prev-button"
            iconTitle={formatText("gallerycarousel.previousImage")}
            onClick={this.onPrevCallback}
            analytics={clickLeftAnalytics}
            aria-label={formatText("mediagallery.previousImage")}
          />

          {showGalleryButton && (
            <UitkButton
              id="galleryCarouselOpenButton"
              className="uitk-gallery-button all-b-padding-two"
              analytics={clickMediaGalleryAnalytics}
              onClick={this.onMediaButtonCallback}
            >
              <span className="is-visually-hidden">Viewing image {currentIndex + 1} of</span>
              {imagesLength}
              <UitkIcon
                name="photo_library"
                title={formatText("gallerycarousel.mediaGalleryButton", imagesLength)}
                id="photo_library"
                shadow={true}
              />
            </UitkButton>
          )}

          <UitkPagingNextButton
            className="uitk-gallery-carousel-button-next"
            iconId="next-button"
            iconTitle={formatText("gallerycarousel.nextImage")}
            onClick={this.onNextCallback}
            analytics={clickRightAnalytics}
            aria-label={formatText("mediagallery.nextImage")}
          />
        </div>
      )
    );
  }
}
